'use client'

import { useQuery } from '@tanstack/react-query'
import { CalendarClock } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@ui/web/components'
import { formatDate } from '@helper/utils/format'
import cn from '@config/tailwind/cn'

import { useAuthClient, useSharedTRPC } from '../context'

interface ScheduledSeatsNoticeProps {
  className?: string
}

/**
 * "X kursi akan aktif 12 Maret 2026" — seats the organization has paid for that have not started.
 *
 * A renewal buys a pool beginning the day the current one ends, so the seats exist, are paid for,
 * and are invisible to every seat read in the product until that date. Without this an admin who
 * has just renewed sees no change anywhere: the ledger still shows the old totals, "Tersedia" does
 * not move, and the only evidence of the purchase is an invoice.
 *
 * Counts UNASSIGNED seats only. A renewal's continuing students are already stamped on their new
 * seats, and the roster's Akses column carries the renewed date for each of them — announcing those
 * here would double-report the same purchase to the same person, and would make the number stop
 * meaning "seats you still have to hand out".
 *
 * Self-gating on two conditions, both of which have to be checked here rather than at the call
 * sites: owner/admin only — a teacher can neither hand out a seat nor buy one, so the notice is
 * noise to them — and at least one unassigned scheduled seat. Every surface that mounts this
 * (billing, the class roster, the dashboard header) would otherwise repeat the same two checks.
 */
export function ScheduledSeatsNotice({ className }: ScheduledSeatsNoticeProps) {
  const authClient = useAuthClient()
  const trpc = useSharedTRPC()

  const { data: memberRole, isPending: isRolePending } = authClient.useActiveMemberRole()
  const isAdminOrOwner = memberRole?.role === 'owner' || memberRole?.role === 'admin'

  const { data: scheduledPools } = useQuery({
    ...trpc.myOrganization.getScheduledSeats.queryOptions(),
    enabled: !isRolePending && isAdminOrOwner,
  })

  if (isRolePending || !isAdminOrOwner) return null

  // One line per pool rather than a single sum: each carries its own activation date, and a total
  // would have to pick one of them to print — the same reason the grant notice lists its pools.
  const pending = (scheduledPools ?? []).filter((pool) => pool.seatsUnassigned > 0)

  if (pending.length === 0) return null

  return (
    <Alert variant='neutral' className={cn(className)}>
      {/* The icon rides inside the title, matching `GrantSeatsNotice` on the billing page — the two
          appear together for a school that renewed early while holding a grant. */}
      <AlertTitle className='srd:flex srd:items-center srd:gap-x-2'>
        <CalendarClock className='srd:size-5 srd:shrink-0' aria-hidden />
        Kursi terjadwal
      </AlertTitle>

      <AlertDescription className='srd:flex srd:flex-col'>
        {pending.map((pool) => (
          <span key={pool.id}>
            {pool.seatsUnassigned} kursi akan aktif {formatDate(pool.startDate, { dateStyle: 'long' })}
          </span>
        ))}
      </AlertDescription>
    </Alert>
  )
}
